import { Tooltip } from 'antd';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { BillRateSourceType } from 'api/project';
import { Avatar } from 'components/atoms/avatar';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { fullName } from 'utils/full-name';
import {
  NameRenderer,
  RateInput,
  ReferenceTeamWrapper,
  TextWrapperWithEllipsis,
  UpdatedByWrapper,
  CostRateWrapper,
} from './styles';
import { RateInfoRowData } from './types';

const EditRateInput = styled(RateInput)`
  outline: none !important;
  ${({ readOnly }) =>
    readOnly
      ? css`
          input {
            cursor: default !important;
          }
        `
      : ''};
`;

export const GetAvatar = ({ data }: { data: RateInfoRowData }) => {
  if (data.sourceType === BillRateSourceType.USER) {
    return (
      <NameRenderer
        logo={<Avatar name={fullName(data?.user)} src={data?.user?.photoUrl} />}
        primaryText={fullName(data?.user)}
      />
    );
  }

  if (data.sourceType === BillRateSourceType.TEAM && 'referenceTeam' in data) {
    return (
      <NameRenderer
        logo={<Avatar name={fullName(data?.user)} src={data?.user?.photoUrl} />}
        primaryText={fullName(data?.user)}
        secondaryText={
          data.referenceTeam ? (
            <ReferenceTeamWrapper>
              <Tooltip
                title={
                  <FormattedMessage id="project.settings.budget.rate.replaced.user.avatar.info" />
                }
                placement="bottomLeft"
                arrowPointAtCenter
              >
                <PlaceholderAvatar isCustomerRole={false} size={12} />
              </Tooltip>
              <TextWrapperWithEllipsis>
                {data?.referenceTeam?.teamName ?? ''}
              </TextWrapperWithEllipsis>
            </ReferenceTeamWrapper>
          ) : null
        }
      />
    );
  }

  if (data.sourceType === BillRateSourceType.TEAM && 'team' in data) {
    return (
      <NameRenderer
        logo={
          <PlaceholderAvatar isCustomerRole={false} name={data.team.teamName} />
        }
        primaryText={data.team.teamName}
      />
    );
  }

  return null;
};

export const LastUpdatedRenderer = ({ data }: { data: RateInfoRowData }) => {
  const lastSaved = moment(data.updatedAt).format('Do MMM YY');

  return (
    <>
      {data.updatedUser ? (
        <UpdatedByWrapper>{`${fullName(
          data?.updatedUser,
        )} on ${lastSaved}`}</UpdatedByWrapper>
      ) : (
        <>-</>
      )}
    </>
  );
};

export const RoleCellRenderer = ({ data }: { data: RateInfoRowData }) => {
  return (
    <NameRenderer
      logo={<Avatar name={data?.role?.roleName} />}
      primaryText={<span>{data?.role?.roleName}</span>}
    />
  );
};

const getSourceType = (data: RateInfoRowData): BillRateSourceType => {
  if ('referenceTeam' in data) {
    return BillRateSourceType.TEAM;
  }

  if ('team' in data) {
    return BillRateSourceType.TEAM;
  }

  return BillRateSourceType.USER;
};

export const HourRateRenderer = ({
  data,
  readonly,
  updateBillRateLookUp,
}: {
  data: RateInfoRowData;
  readonly: boolean | undefined;
  updateBillRateLookUp?: (lookup) => void;
}) => {
  const { sourceId } = data;

  const updateLookUp = (updatedRate) => {
    const sourceType = getSourceType(data);
    const replacedUserId = 'referenceTeam' in data ? data.user.userId : null;
    const lookupKey = `${sourceId}${
      replacedUserId ? `_${replacedUserId}_` : '_'
    }${sourceType}`;

    updateBillRateLookUp?.((lookup) => ({
      ...lookup,
      [lookupKey]: {
        ...lookup[sourceId],
        rate: Number(updatedRate),
        sourceType,
        currency: data.hourRate?.currencyCode,
        ...(replacedUserId ? { replacedUserId } : {}),
      },
    }));
  };

  return (
    <EditRateInput
      onPressEnter={(e) => e.currentTarget.blur()}
      readOnly={readonly}
      min={0}
      formatter={(val) =>
        val ? `${data.hourRate.currency ?? '$'} ${val}` : ''
      }
      onChange={(val) => {
        updateLookUp(val);
      }}
      value={data?.hourRate?.billRate as number}
      placeholder="-"
    />
  );
};

export const CostRateRenderer = ({ data }: { data: RateInfoRowData }) => {
  return (
    <CostRateWrapper>
      {data.costRate ? `$ ${data.costRate}` : '-'}
    </CostRateWrapper>
  );
};
